import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { build, pool } from '@fitogest/reactive-form-builder/utils';
import { environment } from '../../../../../../environments/environment';
import type {
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { OptionsComponent } from '../../../../../common/components/upload-file/upload-file.component';
import { EcorregimenModel } from '../../../../../models/tareas/ecorregimen.model';
import { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import { EcorregimenesFormDefinition } from './form-control/form-definition';

const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const DROPDOWN_REFRESH_FIELD = '__dropdown_refresh';

@Component({
    selector: 'app-ecorregimenes-form',
    standalone: false,
    templateUrl: './ecorregimenes-form.component.html',
    styleUrls: ['./ecorregimenes-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EcorregimenesFormComponent extends BaseForm<EcorregimenModel> implements OnInit {
    public readonly appName = environment.appName;
    public readonly reactiveFormLoadingLabel = 'Preparando formulario de ecorregimenes...';

    public form!: EcorregimenesFormDefinition;
    public override model: EcorregimenModel = {};
    public initialPreviousData: EcorregimenModel | null = null;
    public loadingData = true;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public getRequest = this.tareasApi.ecorregimenes.GET;
    public reactiveFormRecordId = '';
    public showDialogUploadFile = false;

    public optionsUploadFile: OptionsComponent = {
        name: '',
        directory: 'agroambientales',
        multiple: true,
        accept: '.pdf',
        maxFileSize: 2000000,
        mode: 'advanced',
        clientIdResolver: () => this.resolveUploadClientId(),
        uploaded: false,
        uploadedFilesNames: [],
        url: environment.serverUrl + 'ws/index.php?p1=upload&p2=upload_file&folder=agroambientales',
    };

    constructor(
        public override dashboard: DashboardService,
        public tareasApi: TareasApiService,
        public override route: ActivatedRoute,
        public override router: Router,
        public fincasApi: FincasApiService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.ecorregimenes.PUT,
            tareasApi.ecorregimenes.POST,
            'tareas-ecorregimenes',
            `Editar Tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[1]}`,
            `Realizar Tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[1]}`,
            `Duplicar Tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[1]}`,
        );
    }

    public async ngOnInit(): Promise<void> {
        try {
            this.loadingData = true;
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId();
            const initialModel = this.initializeHostModel(this.toEcorregimenModel(this.getModel()));

            this.initialPreviousData = this.getRegisterId()
                ? this.toEcorregimenModel(initialModel)
                : null;

            await this.setupForm();
            this.form.model = this.toEcorregimenModel(this.form.model);
            this.syncModelFromForm();
            this.prepareUploadState();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.cdr.markForCheck();
            console.error('No se ha podido preparar el formulario de ecorregimenes.', error);
        }
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<EcorregimenModel>): void {
        const changedFieldName = String(event.changedField ?? '');

        const stableValue = this.captureStableValueChange(event.value as EcorregimenModel);

        this.form.model = this.toEcorregimenModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();

        if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD || changedFieldName === DROPDOWN_REFRESH_FIELD) {
            this.refreshReactiveDropdownMappings();
            return;
        }

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }

    public reactiveFormChanges(
        formModel: ReactiveFormValue<EcorregimenModel>,
        tag: ReactiveFormChangedField<EcorregimenModel>,
    ): void {
        this.stableValueChanges({
            value: formModel,
            changedField: tag,
            changedFields: [String(tag ?? '')],
            origin: 'user',
        });
    }

    private handleUserValueChange(
        formModel: ReactiveFormValue<EcorregimenModel>,
        tag: ReactiveFormChangedField<EcorregimenModel>,
    ): void {
        const changedFieldName = String(tag ?? '');

        this.form.model = this.toEcorregimenModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();

        if (changedFieldName === 'hora_inicio' || changedFieldName === 'hora_fin') {
            this.updateHorasTotales();
        }

        if (changedFieldName === 'id_finca') {
            this.clearEcorregimenesCascade(['id_sector', 'id_parcelas']);
        }

        if (changedFieldName === 'id_sector') {
            this.clearEcorregimenesCascade(['id_parcelas']);
        }
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<EcorregimenModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            if (event.type === 'dropdownDataResolved' || event.type === 'dropdownRefresh') {
                this.refreshReactiveDropdownMappings();
            }

            return;
        }

        const stableValue = this.captureStableValueChange(event.value as EcorregimenModel);

        this.form.model = this.toEcorregimenModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();
        this.prepareUploadState();
        this.refreshReactiveDropdownMappings();
    }

    public handleEventUpload(event: { originalEvent: unknown; files: File[] }): void {
        if (event.files.length === 0 || !this.form) {
            return;
        }

        const adjuntoGuia = event.files.map((file) => file.name).join(', ');

        this.form.model = this.toEcorregimenModel({
            ...this.form.model,
            adjunto_guia: adjuntoGuia,
        });
        this.syncModelFromForm();
        this.optionsUploadFile.uploaded = true;
        this.optionsUploadFile.uploadedFilesNames = event.files.map((file) => file.name);
        this.showDialogUploadFile = false;
        this.formBuilderRef?.updateForm({ adjunto_guia: adjuntoGuia }, true);
        this.cdr.markForCheck();
    }


    public handleUploadedFileNamesChange(fileNames: string[]): void {
        if (!this.form) {
            return;
        }

        const uploadedFileNames = fileNames
            .map((fileName) => fileName.trim())
            .filter((fileName) => fileName.length > 0);
        const adjuntoGuia = uploadedFileNames.join(', ');

        this.form.model = this.toEcorregimenModel({
            ...this.form.model,
            adjunto_guia: adjuntoGuia,
        });
        this.syncModelFromForm();
        this.optionsUploadFile.uploaded = uploadedFileNames.length > 0;
        this.optionsUploadFile.uploadedFilesNames = uploadedFileNames;
        this.formBuilderRef?.updateForm({ adjunto_guia: adjuntoGuia }, true);
        this.cdr.markForCheck();
    }

    private async setupForm(): Promise<void> {
        this.form = new EcorregimenesFormDefinition(this.getType(), this.model);
        this.form.model = this.toEcorregimenModel(this.form.model);

        await this.initFormData();
    }

    private async initFormData(): Promise<void> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        try {
            await pool(this.getDropdownBuildsForConfiguredCatalogs(), () => this.refreshReactiveDropdownMappings());

            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
            throw error;
        }
    }


    private getDropdownBuildsForConfiguredCatalogs(): ReturnType<typeof build>[] {
        const configuredCatalogs = this.getConfiguredDropdownCatalogs();
        const buildsRequest: ReturnType<typeof build>[] = [];

        if (configuredCatalogs.has('id_finca')) {
            buildsRequest.push(build(this.fincasApi.fincasGET, this.form.fincas, 'nombre'));
        }

        if (configuredCatalogs.has('id_sector')) {
            buildsRequest.push(build(this.fincasApi.sectoresGET, this.form.sectores, 'nombre', true));
        }

        if (configuredCatalogs.has('id_parcelas')) {
            buildsRequest.push(build(
                this.fincasApi.parcelasGET,
                this.form.parcelas,
                this.appName === 'avafitogest' ? this.parcelaLabel : 'nombre',
                true,
            ));
        }

        return buildsRequest;
    }

    private getConfiguredDropdownCatalogs(): Set<string> {
        const dropdownCatalogs = this.form.formFields.dropdownCatalogs;

        return new Set([
            ...(dropdownCatalogs?.critical ?? []),
            ...(dropdownCatalogs?.deferred ?? []),
            ...(dropdownCatalogs?.lazy ?? []),
        ]);
    }

    private clearEcorregimenesCascade(fieldNames: string[]): void {
        const updates: Record<string, unknown> = {};

        for (const fieldName of fieldNames) {
            updates[fieldName] = '';
        }

        this.form.model = this.toEcorregimenModel({
            ...this.form.model,
            ...updates,
        });
        this.syncModelFromForm();
        this.formBuilderRef?.updateForm(updates as Partial<EcorregimenModel>, false);
        this.refreshReactiveDropdownMappings();
    }

    private updateHorasTotales(): void {
        const horasTotales = this.form.calculateHorasTotales();

        if (this.form.model.horas_totales === horasTotales) {
            return;
        }

        this.form.model = this.toEcorregimenModel({
            ...this.form.model,
            horas_totales: horasTotales,
        });
        this.syncModelFromForm();
        this.formBuilderRef?.updateForm({ horas_totales: horasTotales }, true);
    }

    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.cdr.detectChanges();
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
        this.cdr.markForCheck();
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return 'No se ha podido cargar el formulario de ecorregimenes: ' + error.message;
        }

        return 'No se ha podido cargar el formulario de ecorregimenes. Revisa la conexión e inténtalo de nuevo.';
    }

    private resolveUploadClientId(): string {
        const modelClientId = this.resolvePrimitiveId(this.model.id_cliente);

        if (modelClientId.length > 0) {
            return modelClientId;
        }

        return this.resolveClientIdFromFinca(this.model.id_finca);
    }

    private resolveClientIdFromFinca(fincaValue: EcorregimenModel['id_finca']): string {
        if (this.isRecord(fincaValue)) {
            const directClientId = this.resolvePrimitiveId(fincaValue['id_cliente']);

            if (directClientId.length > 0) {
                return directClientId;
            }
        }

        const fincaId = this.resolvePrimitiveId(this.isRecord(fincaValue) ? fincaValue['id'] : fincaValue);

        if (fincaId.length === 0 || !this.form) {
            return '';
        }

        const fincaOptions = [
            ...this.form.fincas.values,
            ...this.form.fincas.filtered,
        ];

        for (const fincaOption of fincaOptions) {
            const sourceFinca = fincaOption.raw ?? fincaOption.value;

            if (!this.isRecord(sourceFinca)) {
                continue;
            }

            const sourceFincaId = this.resolvePrimitiveId(sourceFinca['id']);

            if (sourceFincaId !== fincaId) {
                continue;
            }

            const clientId = this.resolvePrimitiveId(sourceFinca['id_cliente']);

            if (clientId.length > 0) {
                return clientId;
            }
        }

        return '';
    }

    private resolvePrimitiveId(value: unknown): string {
        if (typeof value === 'string' || typeof value === 'number') {
            return String(value).trim();
        }

        if (this.isRecord(value)) {
            return this.resolvePrimitiveId(value['id']);
        }

        return '';
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private prepareUploadState(): void {
        const uploadedFileNames = this.model.adjunto_guia
            ? String(this.model.adjunto_guia).split(',').map((it) => it.trim()).filter((it) => it.length > 0)
            : [];

        this.optionsUploadFile.uploaded = uploadedFileNames.length > 0;
        this.optionsUploadFile.uploadedFilesNames = uploadedFileNames;
    }

    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.refreshExternalDropdownMappings();
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toEcorregimenModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toEcorregimenModel(
        sourceModel: ReactiveFormValue<EcorregimenModel> | Partial<EcorregimenModel> | Record<string, unknown>,
    ): EcorregimenModel {
        const normalizedModel: Record<string, EcorregimenModel[keyof EcorregimenModel]> = {};

        for (const [key, value] of Object.entries(sourceModel)) {
            if (value !== undefined) {
                normalizedModel[key] = value as EcorregimenModel[keyof EcorregimenModel];
            }
        }

        return normalizedModel as EcorregimenModel;
    }

    private parcelaLabel = (p: ParcelasModel): string => {
        const nombre = (p.nombre ?? '').toString().trim();
        const variedad = (p.variedad ?? 'SIN VARIEDAD').toString().trim();

        let zonaRiegoAbrev = '';

        if ((p.cultivo ?? '').toLowerCase() === 'arroz') {
            const zonaRiego = (p.zona_riego ?? '').toString().toLowerCase();

            if (zonaRiego.includes('alta')) {
                zonaRiegoAbrev = 'ZA';
            } else if (zonaRiego.includes('baja')) {
                zonaRiegoAbrev = 'ZB';
            }
        }

        const variedadConZona = zonaRiegoAbrev ? `${variedad} - ${zonaRiegoAbrev}` : variedad;
        const poligono = (p.poligono ?? '').toString().trim();
        const numero = (p.numero ?? '').toString().trim();
        const recinto = (p.recinto ?? '').toString().trim();
        const codigos = [poligono, numero, recinto].filter(Boolean).join('-');
        const superficieRaw = (p.superficie_cultivada ?? p.superficie_sigpac ?? p.superficie ?? '0').toString();
        const superficieNumber = parseFloat(superficieRaw.replace(',', '.')) || 0;
        const superficie = superficieNumber.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const unidades = environment.features.textoSuperficie || 'ha';
        const derecha = [codigos || undefined, `${superficie} ${unidades}`].filter(Boolean).join(' · ');

        return `${nombre} - ${variedadConZona} — ${derecha}`;
    };
}
